import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

import { APP_CONFIG } from '../config/topicConfig';
import type {
  PracticeSession,
  QuestionResult,
  ConceptMastery,
  Question,
  TopicType,
  Difficulty,
} from '../types';
import { DEFAULT_TOPIC_TYPE, isTopicType } from '../types';
import {
  GUEST_PER_SESSION_LIMIT,
  guestHasDailyQuestionsLeft,
  incrementGuestDailyCount,
} from '../utils/guestLimit';
import { calculateAccuracy } from '../utils/helpers';
import { clampCorrectAttempts, toMasteryLabel, toValidDifficulty } from '../utils/masteryHelpers';
import { storage, STORAGE_KEYS } from '../utils/storage';

import { useAuth } from './AuthContext';

interface PracticeContextType {
  currentSession: PracticeSession | null;
  sessions: PracticeSession[];
  conceptMastery: ConceptMastery[];
  guestQuestionCount: number;
  canPracticeMore: boolean;
  currentStreak: number;
  incompleteSession: PracticeSession | null; // For session recovery
  startSession: (session: PracticeSession) => void;
  resumeSession: () => void; // Resume from incomplete session
  endSession: () => Promise<string | null>;
  addQuestionResult: (result: QuestionResult) => void;
  getConceptMastery: (concept: string) => ConceptMastery | undefined;
  updateConceptMastery: (question: Question, isCorrect: boolean) => Promise<void>;
  setCurrentStreak: (streak: number) => void;
  saveSessionProgress: () => Promise<void>; // Save current progress without ending
  clearIncompleteSession: () => void; // Clear recovered session
}

const PracticeContext = createContext<PracticeContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function usePractice(): PracticeContextType {
  const context = useContext(PracticeContext);
  if (!context) {
    throw new Error('usePractice must be used within PracticeProvider');
  }
  return context;
}

interface PracticeProviderProps {
  children: ReactNode;
}

export function PracticeProvider({ children }: PracticeProviderProps): JSX.Element {
  const { user, currentProfile, activeStudent, updateStudentProgress, isGuest } = useAuth();
  const profile = activeStudent || currentProfile;
  const [currentSession, setCurrentSession] = useState<PracticeSession | null>(null);
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [conceptMastery, setConceptMastery] = useState<ConceptMastery[]>([]);
  const [guestQuestionCount, setGuestQuestionCount] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0); // Track current streak in context
  const [incompleteSession, setIncompleteSession] = useState<PracticeSession | null>(null);

  // Check max questions per session for student profiles
  const maxQuestionsPerSession =
    profile && 'preferences' in profile ? profile.preferences?.maxQuestionsPerSession : undefined;

  // Guests are limited both per-session (GUEST_PER_SESSION_LIMIT, based on the
  // current session's question count) and per-day (GUEST_DAILY_LIMIT, resets at
  // local midnight). guestQuestionCount is strictly per-session (see startSession).
  const canPracticeMore = user?.isGuest
    ? (currentSession?.totalQuestions || 0) < GUEST_PER_SESSION_LIMIT &&
      guestHasDailyQuestionsLeft()
    : (currentSession?.totalQuestions || 0) <
      (maxQuestionsPerSession || APP_CONFIG.maxQuestionsPerSession);

  // Load saved data on mount and from Supabase for authenticated users
  useEffect(() => {
    loadLocalData(!!(isGuest || user?.isGuest));

    // Load cloud data for authenticated student users
    if (activeStudent && !isGuest) {
      void loadCloudData(activeStudent.id);
    }
  }, [activeStudent, isGuest, user?.isGuest]);

  const loadLocalData = (guest: boolean): void => {
    const savedSessions = storage.get<PracticeSession[]>(STORAGE_KEYS.PRACTICE_SESSIONS) || [];
    const savedMasteryRaw = storage.get<ConceptMastery[]>(STORAGE_KEYS.CONCEPT_MASTERY) || [];
    const savedIncomplete = storage.get<PracticeSession>(STORAGE_KEYS.INCOMPLETE_SESSION);

    // Sanitize any legacy/invalid mastery records so we never propagate NaN/null values
    // into calculations or Supabase writes.
    const savedMastery: ConceptMastery[] = savedMasteryRaw
      .filter((m) => m && typeof m.concept === 'string' && m.concept.length > 0)
      .map((m) => {
        const totalAttempts = Number.isFinite(m.totalAttempts)
          ? Math.max(0, Math.floor(m.totalAttempts))
          : 0;
        const correctAttemptsRaw = Number.isFinite(m.correctAttempts)
          ? Math.max(0, Math.floor(m.correctAttempts))
          : 0;
        const correctAttempts = clampCorrectAttempts(totalAttempts, correctAttemptsRaw, {
          source: 'local',
          concept: m.concept,
          topicType: m.topicType,
        });
        const accuracy = Number.isFinite(m.accuracy)
          ? Math.max(0, Math.min(100, m.accuracy))
          : totalAttempts > 0
            ? (correctAttempts / totalAttempts) * 100
            : 0;
        const topicType: TopicType = isTopicType(m.topicType) ? m.topicType : DEFAULT_TOPIC_TYPE;

        const legacyLastPracticed =
          typeof m.lastPracticed === 'string' && m.lastPracticed.length > 0
            ? m.lastPracticed
            : '1970-01-01T00:00:00.000Z';

        return {
          ...m,
          topicType,
          totalAttempts,
          correctAttempts,
          accuracy,
          lastPracticed: legacyLastPracticed,
        };
      });

    setSessions(savedSessions);
    setConceptMastery(savedMastery);
    // guestQuestionCount is per-session only; it is not restored from storage.

    // Offer an unfinished session only if it really is unfinished: not already saved as a
    // finished session, not at its question cap, and (for a guest) with questions left today.
    // Anything else is dropped.
    if (savedIncomplete && savedIncomplete.totalQuestions > 0) {
      const alreadySaved = savedSessions.some((s) => s.id === savedIncomplete.id);
      const reachedCap =
        typeof savedIncomplete.maxQuestions === 'number' &&
        savedIncomplete.totalQuestions >= savedIncomplete.maxQuestions;
      const guestOutOfQuestions = guest && !guestHasDailyQuestionsLeft();
      if (alreadySaved || reachedCap || guestOutOfQuestions) {
        storage.remove(STORAGE_KEYS.INCOMPLETE_SESSION);
        // This can run again once the guest is known; clear what an earlier pass offered.
        setIncompleteSession(null);
      } else {
        setIncompleteSession(savedIncomplete);
      }
    }
  };

  const loadCloudData = async (studentId: string): Promise<void> => {
    try {
      const { studentService } = await import('../services/supabaseService');

      // Load practice sessions from Supabase
      const cloudSessions = await studentService.getPracticeSessions(studentId, 50);
      if (cloudSessions.length > 0) {
        // Convert Supabase format to PracticeSession format
        const formattedSessions: PracticeSession[] = cloudSessions.map((s) => ({
          id: s.id as string,
          userId: studentId,
          topicType: s.topic_type as TopicType, // Database column name
          startTime: s.started_at as string,
          endTime: (s.completed_at as string | null) || undefined,
          completed: true,
          totalQuestions: s.questions_total as number,
          correctAnswers: s.questions_correct as number,
          pointsEarned: s.points_earned as number,
          accuracy: s.accuracy as number,
          difficulty: s.difficulty_level as Difficulty,
          questions: [] as QuestionResult[], // Don't load individual questions for performance
        }));
        setSessions(formattedSessions);
      }

      // Load concept progress from Supabase
      const cloudProgress = await studentService.getConceptProgress(studentId);
      if (cloudProgress.length > 0) {
        const formattedMastery: ConceptMastery[] = cloudProgress.map(
          (p: Record<string, unknown>) => {
            const attempts = Number.isFinite(p.attempts)
              ? Math.max(0, Math.floor(p.attempts as number))
              : 0;
            const correctAttemptsRaw = Number.isFinite(p.correct_attempts)
              ? Math.max(0, Math.floor(p.correct_attempts as number))
              : 0;
            const correctAttempts = clampCorrectAttempts(attempts, correctAttemptsRaw, {
              source: 'cloud',
              concept: p.concept as string,
              topic_type: p.topic_type as string,
            });
            const accuracy = attempts > 0 ? (correctAttempts / attempts) * 100 : 0;
            const topicType: TopicType = isTopicType(p.topic_type)
              ? p.topic_type
              : DEFAULT_TOPIC_TYPE;

            return {
              concept: p.concept as string,
              topicType,
              totalAttempts: attempts,
              correctAttempts,
              accuracy,
              currentDifficulty: toValidDifficulty(p.mastery_level as number | null | undefined),
              masteryLevel: toMasteryLabel(accuracy, attempts),
              lastPracticed:
                (p.last_practiced_at as string | null | undefined) || new Date().toISOString(),
            };
          }
        );
        setConceptMastery(formattedMastery);
        storage.set(STORAGE_KEYS.CONCEPT_MASTERY, formattedMastery);
      }
    } catch {
      // Fall back to localStorage data
    }
  };

  // The latest session, updated synchronously with every change. Answers and auto-saves
  // fire in the same tick, before React re-renders, so render closures would be stale.
  const currentSessionRef = useRef<PracticeSession | null>(null);
  // The first Supabase INSERT while it is in flight (resolves to the new row id, or null).
  const insertInFlightRef = useRef<Promise<string | null> | null>(null);
  // The id of the session endSession is finishing. Saves for it are skipped, so a hidden-tab or
  // beforeunload save can't write the finished session back to INCOMPLETE_SESSION.
  const endingSessionIdRef = useRef<string | null>(null);

  const replaceSession = (session: PracticeSession | null): void => {
    currentSessionRef.current = session;
    setCurrentSession(session);
  };

  const startSession = (session: PracticeSession): void => {
    insertInFlightRef.current = null;
    replaceSession(session);
    setIncompleteSession(null); // Clear any previous incomplete session
    storage.remove(STORAGE_KEYS.INCOMPLETE_SESSION);
    // Guest per-session counter resets each session (the daily allowance persists).
    if (user?.isGuest) setGuestQuestionCount(0);
  };

  // Resume from incomplete session
  const resumeSession = (): void => {
    if (incompleteSession) {
      // Another tab may have used the guest's last questions since the dialog was shown.
      if ((isGuest || user?.isGuest) && !guestHasDailyQuestionsLeft()) {
        setIncompleteSession(null);
        storage.remove(STORAGE_KEYS.INCOMPLETE_SESSION);
        return;
      }
      insertInFlightRef.current = null;
      replaceSession(incompleteSession);
      setIncompleteSession(null);
      storage.remove(STORAGE_KEYS.INCOMPLETE_SESSION);
    }
  };

  const sessionRowUpdate = (
    session: PracticeSession
  ): {
    completed_at: string;
    questions_total: number;
    questions_correct: number;
    points_earned: number;
    accuracy: number;
    difficulty_level: number;
  } => ({
    completed_at: new Date().toISOString(),
    questions_total: session.totalQuestions,
    questions_correct: session.correctAnswers,
    points_earned: session.pointsEarned,
    accuracy: calculateAccuracy(session.correctAnswers, session.totalQuestions),
    difficulty_level: session.difficulty,
  });

  // Save session progress without ending (for auto-save)
  // Saves to localStorage every call (synchronously), and to Supabase every question
  const saveSessionProgress = async (): Promise<void> => {
    const snapshot = currentSessionRef.current;
    if (!snapshot || snapshot.totalQuestions === 0) return;
    if (endingSessionIdRef.current === snapshot.id) return;

    // Always save to localStorage first, before any await
    storage.set(STORAGE_KEYS.INCOMPLETE_SESSION, snapshot);

    // Save to Supabase every question (for authenticated students)
    if (!activeStudent || isGuest) return;

    try {
      const { studentService } = await import('../services/supabaseService');
      if (endingSessionIdRef.current === snapshot.id) return;

      // Another save may have started the INSERT while we loaded the module.
      let inFlight = insertInFlightRef.current;
      if (
        !snapshot.supabaseSessionId &&
        !inFlight &&
        !currentSessionRef.current?.supabaseSessionId
      ) {
        const localId = snapshot.id;
        inFlight = (async (): Promise<string | null> => {
          try {
            const sessionId = await studentService.savePracticeSession({
              student_id: activeStudent.id,
              parent_id: activeStudent.parentId,
              topic_type: snapshot.topicType,
              started_at: snapshot.startTime,
              ...sessionRowUpdate(snapshot),
            });
            const latest = currentSessionRef.current;
            if (sessionId && latest && latest.id === localId) {
              const merged = { ...latest, supabaseSessionId: sessionId };
              currentSessionRef.current = merged;
              setCurrentSession((prev) =>
                prev && prev.id === localId ? { ...prev, supabaseSessionId: sessionId } : prev
              );
              if (endingSessionIdRef.current !== localId) {
                storage.set(STORAGE_KEYS.INCOMPLETE_SESSION, merged);
              }
            }
            return sessionId || null;
          } catch (error) {
            console.error('Auto-save to Supabase failed:', error);
            return null;
          } finally {
            if (insertInFlightRef.current === inFlight) insertInFlightRef.current = null;
          }
        })();
        insertInFlightRef.current = inFlight;
        const insertedId = await inFlight;
        // Answers that landed during the INSERT are sent as an UPDATE.
        const latest = currentSessionRef.current;
        if (
          insertedId &&
          latest &&
          latest.id === snapshot.id &&
          latest.totalQuestions > snapshot.totalQuestions
        ) {
          await studentService.updatePracticeSession(insertedId, sessionRowUpdate(latest));
        }
        return;
      }

      // An INSERT is in flight: wait for it, then UPDATE with the latest values.
      if (!snapshot.supabaseSessionId && inFlight) await inFlight;

      const latest = currentSessionRef.current;
      if (latest && latest.id === snapshot.id && latest.supabaseSessionId) {
        await studentService.updatePracticeSession(
          latest.supabaseSessionId,
          sessionRowUpdate(latest)
        );
      }
    } catch (error) {
      console.error('Auto-save to Supabase failed:', error);
    }
  };

  // Clear incomplete session (user dismissed recovery dialog)
  const clearIncompleteSession = (): void => {
    setIncompleteSession(null);
    storage.remove(STORAGE_KEYS.INCOMPLETE_SESSION);
  };

  const endSession = async (): Promise<string | null> => {
    endingSessionIdRef.current = currentSessionRef.current?.id ?? null;
    // Let an in-flight first INSERT finish, so the end can't create a second row.
    if (insertInFlightRef.current) await insertInFlightRef.current;
    const currentSession = currentSessionRef.current;
    if (!currentSession || !profile) {
      endingSessionIdRef.current = null;
      return null;
    }

    // Clear incomplete session since we're properly ending
    storage.remove(STORAGE_KEYS.INCOMPLETE_SESSION);

    // Don't save empty sessions (no questions answered)
    if (currentSession.totalQuestions === 0) {
      replaceSession(null);
      return null;
    }

    const finalSession: PracticeSession = {
      ...currentSession,
      endTime: new Date().toISOString(),
      completed: true,
      accuracy: calculateAccuracy(currentSession.correctAnswers, currentSession.totalQuestions),
    };

    // Update sessions
    const updatedSessions = [...sessions, finalSession];
    setSessions(updatedSessions);
    storage.set(STORAGE_KEYS.PRACTICE_SESSIONS, updatedSessions);

    // Track the Supabase session ID so callers can load rich session details.
    let savedSessionId: string | null = finalSession.supabaseSessionId || null;

    // Update profile stats (only for Student and Guest users who have practice stats)
    if (profile && (profile.role === 'student' || profile.role === 'guest')) {
      const updates = {
        sessionsCompleted: profile.sessionsCompleted + 1,
        totalPoints: profile.totalPoints + finalSession.pointsEarned,
        correctAnswers: profile.correctAnswers + finalSession.correctAnswers,
        totalQuestions: profile.totalQuestions + finalSession.totalQuestions,
        accuracy: calculateAccuracy(
          profile.correctAnswers + finalSession.correctAnswers,
          profile.totalQuestions + finalSession.totalQuestions
        ),
      };

      if (isGuest) {
        // Guest mode - updates handled by context localStorage
        storage.set('current_profile', { ...profile, ...updates });
      } else if (activeStudent && profile.role === 'student') {
        // For student profiles, save session to Supabase and update progress
        try {
          const { studentService } = await import('../services/supabaseService');

          let sessionId: string | null = savedSessionId;
          // Same columns as the auto-save, stamped with the end time.
          const finalRow = {
            ...sessionRowUpdate(finalSession),
            completed_at: finalSession.endTime || new Date().toISOString(),
          };

          if (sessionId) {
            // UPDATE existing session (was auto-saved earlier)
            await studentService.updatePracticeSession(sessionId, finalRow);
          } else {
            // INSERT new session (first time saving to Supabase)
            sessionId = await studentService.savePracticeSession({
              student_id: activeStudent.id,
              parent_id: activeStudent.parentId,
              topic_type: finalSession.topicType,
              started_at: finalSession.startTime,
              ...finalRow,
            });
          }

          savedSessionId = sessionId;

          // Save individual question results if we have a session ID
          if (sessionId && finalSession.questions.length > 0) {
            const questionResults = finalSession.questions.map(
              (q: QuestionResult, index: number) => ({
                questionNumber: index + 1,
                questionText: q.question.question,
                questionType: finalSession.topicType,
                concept: q.question.concept || finalSession.topicType,
                difficulty: finalSession.difficulty,
                options: q.question.options || [],
                correctAnswer: q.question.correctAnswer,
                userAnswer: q.userAnswer,
                isCorrect: q.isCorrect,
                timeSpentMs: q.timeSpent,
                attempts: q.attempts,
              })
            );

            await studentService.saveQuestionResults(sessionId, activeStudent.id, questionResults);
          }

          // Update student progress
          await updateStudentProgress(activeStudent.id, {
            pointsEarned: finalSession.pointsEarned,
            questionsTotal: finalSession.totalQuestions,
            questionsCorrect: finalSession.correctAnswers,
            newStreak: currentStreak, // Use the context's current streak from this session
          });
        } catch (error) {
          console.error('Failed to save session:', error);
          // Still update locally even if Supabase save fails
        }
      }
    }

    replaceSession(null);

    return savedSessionId;
  };

  const addQuestionResult = (result: QuestionResult): void => {
    const latest = currentSessionRef.current;
    if (!latest) return;

    const updatedSession: PracticeSession = {
      ...latest,
      questions: [...latest.questions, result],
      totalQuestions: latest.totalQuestions + 1,
      correctAnswers: result.isCorrect ? latest.correctAnswers + 1 : latest.correctAnswers,
      pointsEarned: result.isCorrect
        ? latest.pointsEarned + APP_CONFIG.pointsPerCorrectAnswer
        : latest.pointsEarned,
    };

    replaceSession(updatedSession);

    // Update guest counters: per-session (in-memory) and the daily allowance.
    if (user?.isGuest) {
      setGuestQuestionCount((c) => c + 1);
      // Count against today's daily allowance (resets at local midnight).
      incrementGuestDailyCount();
    }

    // Update concept mastery
    void updateConceptMastery(result.question, result.isCorrect);
  };

  const getConceptMastery = (concept: string): ConceptMastery | undefined => {
    return conceptMastery.find((cm) => cm.concept === concept);
  };

  const toDbTopicType = (question: Question): Exclude<TopicType, 'arithmetic'> | null => {
    if (question.topicType !== 'arithmetic') {
      return question.topicType;
    }

    const text = question.question;
    if (text.includes('÷') || text.includes('/')) return 'division';
    if (text.includes('×') || text.includes('*')) return 'multiplication';
    // Check subtraction before plus? doesn't matter as they are exclusive
    if (text.includes('-') || text.includes('−')) return 'subtraction';
    if (text.includes('+')) return 'addition';

    if (import.meta.env.DEV) {
      console.warn('[PracticeContext] Failed to classify arithmetic question for DB topic type', {
        questionId: question.id,
        topicType: question.topicType,
        text,
      });
    }

    return null;
  };

  /**
   * Update concept mastery statistics for a given question result.
   *
   * Note: This function intentionally takes the full {@link Question} object
   * instead of just a concept string. For arithmetic practice, the Question
   * carries the original `topicType` (often the generic `"arithmetic"`) and
   * the rendered question text, which are both required to determine the
   * correct database `topic_type` (e.g. `"addition"`, `"subtraction"`,
   * `"multiplication"`, `"division"`). That mapping logic lives in
   * {@link toDbTopicType}, so callers must provide the full Question context.
   */
  const updateConceptMastery = async (question: Question, isCorrect: boolean): Promise<void> => {
    const concept = question.concept;
    const existing = conceptMastery.find((cm) => cm.concept === concept);

    let updated: ConceptMastery;
    if (existing) {
      updated = {
        ...existing,
        totalAttempts: existing.totalAttempts + 1,
        correctAttempts: existing.correctAttempts + (isCorrect ? 1 : 0),
        accuracy: calculateAccuracy(
          existing.correctAttempts + (isCorrect ? 1 : 0),
          existing.totalAttempts + 1
        ),
        lastPracticed: new Date().toISOString(),
      };
    } else {
      updated = {
        concept,
        topicType: question.topicType,
        totalAttempts: 1,
        correctAttempts: isCorrect ? 1 : 0,
        accuracy: isCorrect ? 100 : 0,
        currentDifficulty: 1,
        masteryLevel: 'beginner',
        lastPracticed: new Date().toISOString(),
      };
    }

    // Update mastery level based on accuracy
    if (updated.accuracy >= 90 && updated.totalAttempts >= 10) {
      updated.masteryLevel = 'mastered';
    } else if (updated.accuracy >= 80) {
      updated.masteryLevel = 'proficient';
    } else if (updated.accuracy >= 70) {
      updated.masteryLevel = 'practicing';
    } else if (updated.totalAttempts >= 5) {
      updated.masteryLevel = 'learning';
    }

    const updatedMastery = existing
      ? conceptMastery.map((cm) => (cm.concept === concept ? updated : cm))
      : [...conceptMastery, updated];

    setConceptMastery(updatedMastery);
    storage.set(STORAGE_KEYS.CONCEPT_MASTERY, updatedMastery);

    // Save to Supabase for authenticated users
    if (activeStudent && !isGuest) {
      try {
        const dbTopicType = toDbTopicType(question);
        if (!dbTopicType) {
          return;
        }
        const { studentService } = await import('../services/supabaseService');
        await studentService.saveConceptProgress(activeStudent.id, dbTopicType, concept, {
          masteryLabel: updated.masteryLevel,
          score: Math.round(updated.accuracy),
          attempts: updated.totalAttempts,
          successes: updated.correctAttempts,
          lastAttempt: updated.lastPracticed,
        });
      } catch {
        // Silent fail - local data already updated
      }
    }
  };

  const value: PracticeContextType = {
    currentSession,
    sessions,
    conceptMastery,
    guestQuestionCount,
    canPracticeMore,
    currentStreak,
    incompleteSession,
    startSession,
    resumeSession,
    endSession,
    addQuestionResult,
    getConceptMastery,
    updateConceptMastery,
    setCurrentStreak,
    saveSessionProgress,
    clearIncompleteSession,
  };

  return <PracticeContext.Provider value={value}>{children}</PracticeContext.Provider>;
}
